import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import assetsPath from '../../index.js';

/**
 * Nordnet card offer atom. Used in offers molecules.
 *
 * @prop title The card offer text container title.
 * @prop icon The card offer text container icon.
 * @prop customizedClass The card offer text container html class.
 * @prop content The card offer text container content.
 */
@customElement('nn-card-offer-text')
export class CardOfferText extends LitElement {
  static styles = css`
    div body {
      margin: 0;
    }
    .background-in-grey {
      background: var(--nn-black-12, #f7f8fb);
    }

    .link,
    .link:active,
    .link:focus,
    .link:visited,
    .link:hover {
      color: var(--nn-black, #000);
      display: inline;
    }

    .block--refurbish {
      align-items: center;
      background: var(--block-refurbish-color, #5ac834);
      border-radius: 32px;
      color: var(--white, #fff);
      display: flex;
      font-size: calc(16px * 0.75);
      gap: 5px;
      height: 20px;
      line-height: 1;
      justify-content: flex-start;
      padding: 0 6px;
      width: fit-content;
    }
    .block--refurbish .img-responsive {
      position: relative;
      top: 1px;
    }
    .icon-inline {
      background: none;
      border: 0;
      color: var(--nn-black, #000);
      display: flex;
      border-radius: 0;
      box-shadow: none;
      font-family: 'font-medium';
      font-size: 16px;
      justify-content: left;
      line-height: 1;
      margin: 0;
      padding: 0;
      text-align: left;
      text-decoration: none;
      text-transform: none;
      vertical-align: inherit;
    }
    .icon-inline--image {
      flex-basis: 20px;
      flex-shrink: 0;
      height: auto;
      max-width: 20px;
    }
    .icon-inline--text {
      padding: 0 0 0 8px;
    }
    .icon-inline--text_title {
      color: var(--nn-black, #000);
      font-family: 'font-heavy';
      font-size: 0.75em;
      line-height: 1.15;
      padding: 0;
    }
    .icon-inline.multiple {
      align-items: start;
      max-width: 100%;
      margin-bottom: 15px;
      width: 265px;
    }
    .multiple .icon-inline--text_title {
      color: var(--nn-black, #000);
      font-size: 1.25em;
      line-height: 1.143;
      padding: 0 0 12px;
    }
    .icon-inline.multiple .listing {
      margin: 0 0 12px;
      padding: 0;
    }
    .icon-inline.multiple .listing-item {
      color: var(--nn-grey-3, #494949);
      font-family: 'font-light';
      font-size: 14px;
      line-height: 1.167;
      list-style-type: none;
      margin: 0;
      padding: 9px 0;
      position: relative;
    }
    .icon-inline.multiple .listing-item:before {
      background: var(--nn-black-32, #cac8c8);
      content: '';
      display: block;
      height: 1px;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }
    .icon-inline.multiple .listing-item:last-child {
      padding: 8px 0 0;
    }
    .icon-inline.multiple .listing-item b {
      font-family: 'font-heavy';
      font-weight: 400;
    }
    .multiple .block--refurbish {
      display: inline-flex;
    }
    .multiple .link {
      color: var(--nn-grey-3, #494949);
    }
    .multiple .list {
      margin: 0;
      padding: 0px;
    }
    .multiple .list-item {
      color: var(--nn-grey-3, #494949);
      font-family: 'font-light';
      font-size: 1em;
      list-style-type: none;
      margin: 0;
      padding: 0 0 4px 16px;
      position: relative;
    }
    .multiple .list-item:before {
      background: var(--nn-grey-3, #494949);
      border-radius: 50%;
      content: '';
      display: block;
      height: 3px;
      width: 3px;
      position: absolute;
      top: 6px;
      left: 4px;
    }
    .multiple .bullet.list-item {
      padding-left: 0;
      padding-bottom: 15px;
      list-style: unset;
      font-size: 16px;
      line-height: calc(2px + 2ex);
    }
    .multiple .bullet.list-item:before {
      content: none;
    }
    .listing-item .anchor,
    .listing-item .anchor:active,
    .listing-item .anchor:focus,
    .listing-item .anchor:visited,
    .listing-item .anchor:hover {
      background: none;
      border: 0;
      box-shadow: none;
      color: var(--nn-bleu-hyperlien, #3695ec);
      cursor: pointer;
      display: inline-block;
      font-family: 'font-light';
      font-size: 1em;
      margin: 0;
      padding: 0;
      text-decoration: none;
      transition: var(--nn-transition, 0.3s);
    }
    .listing-item .anchor b,
    .listing-item .anchor:active b,
    .listing-item .anchor:focus b,
    .listing-item .anchor:visited b,
    .listing-item .anchor:hover b {
      font-family: 'font-heavy';
      font-weight: 400;
    }
    .listing-item .anchor:hover {
      text-decoration: underline;
    }
    .listing-item .anchor-target {
      color: var(--nn-grey-3, #494949);
    }
    .listing-item .bolder {
      font-family: 'font-medium';
    }
    .listing-item .bolder b {
      font-family: 'font-heavy';
      font-weight: 400;
    }
  `;

  @property({ type: String }) title = '';

  @property({ type: String }) customizedClass = '';

  @property({ type: String }) icon = '';

  @property({ type: String }) content = '';

  render() {
    return html`
      <div class="icon-inline multiple">
        <img
          class="icon-inline--image"
          src="${assetsPath}/icons/${this.icon}"
          alt=""
          aria-hidden="true"
          loading="lazy"
        />
        <div class="icon-inline--text">
          <div class="icon-inline--text_title">
            ${this.parseHtml(this.title)}
          </div>
          <div class="icon-inline--text_text listing">
            ${this.parseHtml(this.content)}
          </div>
        </div>
      </div>
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  parseHtml(str?: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }
}
